import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import type { CoreApiError } from '@lqa-frontend/api-services';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
} from '@lqa-frontend/api-services';

export type UpdateRosterStatus = 'approved' | 'denied';

export type UpdateRosterStatusVariables = {
	rosterId: number;
	consultantId: number;
	status: UpdateRosterStatus;
	adminNotes: string;
};

export type UpdateRosterStatusResponse = {
	message: string;
};

export type UseUpdateRosterStatusMutationOptions = UseMutationOptions<
	AxiosResponse<UpdateRosterStatusResponse>,
	AxiosError<CoreApiError>,
	UpdateRosterStatusVariables
>;

/**
 * `PUT /v2/roster/update-status/{rosterId}` — flips a roster's status to
 * `approved` or `denied` and optionally records an admin note. The
 * backend triggers the corresponding email notification.
 *
 * On success, invalidates everything that depends on the affected
 * roster: the rosters list, this roster's detail, its audit log, and
 * the consultant's overview lookup and statuses (so the year-status dots
 * and header badge stay in sync).
 */
export function useUpdateRosterStatusMutation(
	options?: UseUpdateRosterStatusMutationOptions
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: coreApiQueryKey(endpoints.roster.updateStatus(0), 'put'),
		mutationFn: async ({ rosterId, status, adminNotes, consultantId }) =>
			coreApiClient.put<UpdateRosterStatusResponse>(
				endpoints.roster.updateStatus(rosterId),
				{ status, adminNotes, consultantId }
			),
		...options,
		onSuccess: async (data, variables, onMutateResult, context) => {
			// Important: invalidation keys must match the SHAPE used by
			// each query — we key with `endpoints.roster.{logs|overview|
			// detail}(0)` and pass the real id as a separate tuple element,
			// so the invalidation must do the same. Baking the id into the
			// URL slot here would change the first key element and the
			// prefix match would silently miss → queries never refetch and
			// the new admin note / status would never appear in the Roster
			// log tab.
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: coreApiQueryKey(endpoints.roster.index),
				}),
				queryClient.invalidateQueries({
					queryKey: coreApiQueryKey(
						endpoints.roster.detail(0),
						'get',
						variables.rosterId
					),
				}),
				queryClient.invalidateQueries({
					queryKey: coreApiQueryKey(
						endpoints.roster.logs(0),
						'get',
						variables.rosterId
					),
				}),
				queryClient.invalidateQueries({
					queryKey: coreApiQueryKey(endpoints.roster.overview(0), 'get'),
				}),
				queryClient.invalidateQueries({
					queryKey: coreApiQueryKey(
						endpoints.roster.statuses(0),
						'get',
						variables.consultantId
					),
				}),
			]);

			options?.onSuccess?.(data, variables, onMutateResult, context);
		},
	});
}
